import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useScrollAnimation, useStaggerAnimation } from '../../hooks/useScrollAnimation';

const categories = [
  { id: 'all', label: 'All Equipment', icon: 'ri-apps-line' },
  { id: 'lights', label: 'Grow Lights', icon: 'ri-sun-line' },
  { id: 'soils', label: 'Soils & Media', icon: 'ri-plant-line' },
  { id: 'fertilizers', label: 'Organic Fertilizers', icon: 'ri-flask-line' },
  { id: 'equipment', label: 'Grow Equipment', icon: 'ri-tools-line' },
  { id: 'climate', label: 'Climate Control', icon: 'ri-temp-hot-line' },
];

const products = [
  {
    id: 1,
    category: 'lights',
    name: 'Full Spectrum LED Grow Light 600W',
    price: 189.99,
    originalPrice: 249.99,
    badge: 'Best Seller',
    badgeColor: 'bg-[#D4AF37]',
    rating: 4.9,
    reviews: 312,
    description: 'Professional-grade full spectrum LED panel covering a 4×4 ft canopy. Mimics natural sunlight for explosive growth and dense flowering.',
    features: ['Full Spectrum 3000K–6500K', 'Dimmable 10–100%', 'Low heat output', 'Covers 4×4 ft'],
    image: 'https://public.readdy.ai/ai/img_res/a823f64ef30f58b729f076f3bc851211.jpg',
  },
  {
    id: 2,
    category: 'lights',
    name: 'CMH 315W Ceramic Metal Halide Kit',
    price: 229.99,
    originalPrice: 299.99,
    badge: 'Pro Choice',
    badgeColor: 'bg-[#2D5F3F]',
    rating: 4.8,
    reviews: 198,
    description: 'CMH technology delivers the closest spectrum to natural sunlight. Ideal for both vegetative and flowering stages with a single bulb.',
    features: ['315W CMH Bulb Included', 'Full UV spectrum', 'Reflector hood included', 'Ballast included'],
    image: 'https://readdy.ai/api/search-image?query=CMH%20ceramic%20metal%20halide%20grow%20light%20kit%20with%20reflector%20hood%20and%20ballast%20on%20clean%20white%20surface%20professional%20product%20photography%20warm%20golden%20light%20glow%20horticulture%20equipment%20premium%20quality%20indoor%20growing&width=500&height=500&seq=grow_light_002&orientation=squarish',
  },
  {
    id: 3,
    category: 'lights',
    name: 'Quantum Board LED 240W Samsung LM301B',
    price: 159.99,
    originalPrice: 199.99,
    badge: 'Energy Saver',
    badgeColor: 'bg-emerald-600',
    rating: 4.7,
    reviews: 245,
    description: 'Ultra-efficient quantum board using Samsung LM301B diodes. Whisper-quiet passive cooling with exceptional PAR output per watt.',
    features: ['Samsung LM301B diodes', 'Passive cooling', '2.7 µmol/J efficiency', 'Daisy-chain ready'],
    image: 'https://readdy.ai/api/search-image?query=quantum%20board%20LED%20grow%20light%20panel%20with%20Samsung%20diodes%20flat%20white%20board%20glowing%20soft%20white%20light%20on%20clean%20white%20background%20professional%20product%20photography%20modern%20horticulture%20technology%20premium%20indoor%20growing%20equipment&width=500&height=500&seq=grow_light_003&orientation=squarish',
  },
  {
    id: 4,
    category: 'soils',
    name: 'Premium Organic Cannabis Potting Mix',
    price: 34.99,
    originalPrice: 44.99,
    badge: 'Organic',
    badgeColor: 'bg-[#2D5F3F]',
    rating: 4.9,
    reviews: 421,
    description: 'Specially blended organic soil mix with perlite, coco coir, and worm castings. Pre-amended with slow-release nutrients for the first 4–6 weeks.',
    features: ['Pre-amended formula', 'Excellent drainage', 'pH balanced 6.2–6.8', '20L bag'],
    image: 'https://readdy.ai/api/search-image?query=premium%20organic%20cannabis%20potting%20soil%20mix%20in%20open%20bag%20showing%20rich%20dark%20earthy%20texture%20with%20perlite%20visible%20on%20clean%20white%20background%20professional%20product%20photography%20natural%20organic%20gardening%20supplies%20earthy%20tones&width=500&height=500&seq=grow_soil_001&orientation=squarish',
  },
  {
    id: 5,
    category: 'soils',
    name: 'Coco Coir & Perlite Blend 70/30',
    price: 24.99,
    originalPrice: 32.99,
    badge: 'Hydro Friendly',
    badgeColor: 'bg-teal-600',
    rating: 4.7,
    reviews: 289,
    description: 'Buffered and washed coco coir blended with 30% perlite for optimal air-to-water ratio. Perfect for hydroponic and semi-hydroponic grows.',
    features: ['Pre-buffered & washed', '70/30 coco/perlite', 'Neutral pH', 'Reusable medium'],
    image: 'https://readdy.ai/api/search-image?query=coco%20coir%20and%20perlite%20growing%20medium%20blend%20in%20open%20bag%20showing%20light%20brown%20fibrous%20texture%20with%20white%20perlite%20pieces%20on%20clean%20white%20background%20professional%20product%20photography%20hydroponic%20growing%20supplies%20natural%20material&width=500&height=500&seq=grow_soil_002&orientation=squarish',
  },
  {
    id: 6,
    category: 'soils',
    name: 'Worm Castings Super Soil Booster',
    price: 19.99,
    originalPrice: 27.99,
    badge: 'HR Boss Pick',
    badgeColor: 'bg-[#D4AF37]',
    rating: 5.0,
    reviews: 178,
    description: 'Pure vermicompost worm castings — nature\'s perfect slow-release fertilizer. Improves soil structure, microbial life, and nutrient availability.',
    features: ['100% pure castings', 'Microbial rich', 'Odor-free', 'Safe for seedlings'],
    image: 'https://readdy.ai/api/search-image?query=pure%20worm%20castings%20vermicompost%20in%20open%20bag%20showing%20rich%20dark%20brown%20fine%20texture%20on%20clean%20white%20background%20professional%20product%20photography%20organic%20soil%20amendment%20natural%20fertilizer%20premium%20gardening%20product&width=500&height=500&seq=grow_soil_003&orientation=squarish',
  },
  {
    id: 7,
    category: 'fertilizers',
    name: 'Organic 3-Part Nutrient System',
    price: 79.99,
    originalPrice: 109.99,
    badge: 'Complete Kit',
    badgeColor: 'bg-[#2D5F3F]',
    rating: 4.8,
    reviews: 356,
    description: 'Full-cycle organic nutrient trio: Grow, Bloom, and Micro. Derived from kelp, fish meal, and bat guano for explosive organic results.',
    features: ['Veg + Bloom + Micro', 'OMRI listed organic', 'Kelp & fish derived', '1L each bottle'],
    image: 'https://readdy.ai/api/search-image?query=three%20organic%20plant%20nutrient%20bottles%20grow%20bloom%20micro%20set%20on%20clean%20white%20marble%20surface%20with%20green%20cannabis%20leaves%20professional%20product%20photography%20premium%20organic%20fertilizer%20system%20natural%20wellness%20gardening&width=500&height=500&seq=grow_fert_001&orientation=squarish',
  },
  {
    id: 8,
    category: 'fertilizers',
    name: 'Bat Guano High-Phosphorus Bloom Booster',
    price: 22.99,
    originalPrice: 29.99,
    badge: 'Bloom Power',
    badgeColor: 'bg-amber-600',
    rating: 4.6,
    reviews: 203,
    description: 'High-phosphorus bat guano concentrate for explosive flowering. Promotes dense, resinous buds and enhances terpene production naturally.',
    features: ['High-P formula 0-10-0', 'Enhances terpenes', 'Water soluble', 'OMRI certified'],
    image: 'https://readdy.ai/api/search-image?query=bat%20guano%20organic%20fertilizer%20bag%20with%20cannabis%20flower%20bud%20beside%20it%20on%20clean%20white%20background%20professional%20product%20photography%20natural%20organic%20bloom%20booster%20premium%20gardening%20supply%20earthy%20packaging&width=500&height=500&seq=grow_fert_002&orientation=squarish',
  },
  {
    id: 9,
    category: 'fertilizers',
    name: 'Mycorrhizal Root Inoculant',
    price: 29.99,
    originalPrice: 39.99,
    badge: 'Root Booster',
    badgeColor: 'bg-[#2D5F3F]',
    rating: 4.9,
    reviews: 267,
    description: 'Beneficial mycorrhizal fungi that colonize roots and dramatically increase nutrient and water uptake. Essential for any organic grow.',
    features: ['9 species of mycorrhizae', 'Trichoderma included', 'Works in soil & coco', 'Treats 50+ plants'],
    image: 'https://readdy.ai/api/search-image?query=mycorrhizal%20root%20inoculant%20powder%20in%20small%20jar%20with%20healthy%20white%20cannabis%20roots%20visible%20on%20clean%20white%20background%20professional%20product%20photography%20organic%20root%20booster%20natural%20plant%20biology%20premium%20growing%20supplement&width=500&height=500&seq=grow_fert_003&orientation=squarish',
  },
  {
    id: 10,
    category: 'equipment',
    name: '4×4 ft Professional Grow Tent Kit',
    price: 149.99,
    originalPrice: 199.99,
    badge: 'Complete Setup',
    badgeColor: 'bg-[#2D5F3F]',
    rating: 4.8,
    reviews: 389,
    description: 'Heavy-duty 600D mylar grow tent with double-stitched seams, multiple ports, and tool pouches. Maximizes light reflection for bigger yields.',
    features: ['600D Oxford canvas', '98% reflective mylar', 'Multiple duct ports', 'Tool pouches included'],
    image: 'https://readdy.ai/api/search-image?query=professional%20indoor%20cannabis%20grow%20tent%20open%20showing%20reflective%20mylar%20interior%20with%20plants%20inside%20on%20clean%20white%20background%20product%20photography%20premium%20quality%20grow%20room%20setup%20modern%20horticulture%20equipment&width=500&height=500&seq=grow_equip_001&orientation=squarish',
  },
  {
    id: 11,
    category: 'equipment',
    name: 'Digital pH & EC Meter Combo',
    price: 49.99,
    originalPrice: 69.99,
    badge: 'Essential',
    badgeColor: 'bg-[#D4AF37]',
    rating: 4.7,
    reviews: 512,
    description: 'Precision digital pH and EC/TDS meter combo. Auto-calibration, waterproof design, and ATC for accurate readings every time.',
    features: ['±0.01 pH accuracy', 'Auto-calibration', 'Waterproof IP67', 'ATC technology'],
    image: 'https://readdy.ai/api/search-image?query=digital%20pH%20meter%20and%20EC%20TDS%20meter%20combo%20set%20on%20clean%20white%20surface%20with%20water%20droplets%20professional%20product%20photography%20precision%20gardening%20tools%20modern%20horticulture%20equipment%20minimalist%20white%20background&width=500&height=500&seq=grow_equip_002&orientation=squarish',
  },
  {
    id: 12,
    category: 'climate',
    name: 'Inline Fan & Carbon Filter Combo 6"',
    price: 119.99,
    originalPrice: 159.99,
    badge: 'Odor Control',
    badgeColor: 'bg-slate-600',
    rating: 4.8,
    reviews: 298,
    description: 'Whisper-quiet 6" inline fan paired with activated carbon filter. Eliminates odors completely while maintaining perfect airflow and temperature.',
    features: ['400 CFM airflow', 'Australian activated carbon', 'Ultra-quiet motor', 'Includes ducting'],
    image: 'https://readdy.ai/api/search-image?query=inline%20fan%20and%20carbon%20filter%20combo%20set%20for%20indoor%20growing%20on%20clean%20white%20background%20professional%20product%20photography%20ventilation%20equipment%20modern%20horticulture%20climate%20control%20system%20premium%20quality&width=500&height=500&seq=grow_climate_001&orientation=squarish',
  },
];

const tips = [
  {
    icon: 'ri-sun-line',
    color: '#D4AF37',
    title: 'Lighting Schedule',
    tip: 'Vegetative stage: 18 hrs light / 6 hrs dark. Flowering stage: 12 hrs / 12 hrs. Consistency is key — use a timer.',
  },
  {
    icon: 'ri-drop-line',
    color: '#2D5F3F',
    title: 'Watering Wisdom',
    tip: 'Water when the top inch of soil is dry. Always pH your water to 6.0–7.0 for soil, 5.5–6.5 for coco/hydro.',
  },
  {
    icon: 'ri-temp-hot-line',
    color: '#D4AF37',
    title: 'Temperature & Humidity',
    tip: 'Veg: 70–85°F, 50–70% RH. Flower: 65–80°F, 40–50% RH. Late flower: drop to 30–40% RH to prevent mold.',
  },
  {
    icon: 'ri-flask-line',
    color: '#2D5F3F',
    title: 'Feeding Schedule',
    tip: 'Start nutrients at ¼ strength and increase gradually. Always feed, water, water — never feed every watering.',
  },
];

export default function GrowPage() {
  const [activeCategory, setActiveCategory] = useState('all');
  const [wishlist, setWishlist] = useState<number[]>([]);

  const heroAnim = useScrollAnimation({ threshold: 0.05 });
  const catsAnim = useScrollAnimation({ threshold: 0.1 });
  const productsAnim = useStaggerAnimation(12, { threshold: 0.05 });
  const tipsAnim = useStaggerAnimation(4, { threshold: 0.1 });
  const ctaAnim = useScrollAnimation({ threshold: 0.1 });

  const filtered = activeCategory === 'all'
    ? products
    : products.filter((p) => p.category === activeCategory);

  const toggleWishlist = (id: number) => {
    setWishlist((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">

      {/* Hero */}
      <section className="relative h-[400px] sm:h-[500px] lg:h-[600px] w-full flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=stunning%20professional%20indoor%20cannabis%20grow%20room%20with%20rows%20of%20healthy%20lush%20green%20plants%20under%20bright%20LED%20grow%20lights%20modern%20high%20tech%20cultivation%20facility%20clean%20organized%20space%20warm%20golden%20light%20glow%20professional%20photography%20premium%20horticulture%20setup&width=1920&height=700&seq=grow_hero_001&orientation=landscape"
            alt="Professional Grow Room"
            className="w-full h-full object-cover object-top scale-105 animate-[kenburns_20s_ease-in-out_infinite_alternate]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/20"></div>
        </div>
        <div
          ref={heroAnim.ref as React.RefObject<HTMLDivElement>}
          className="relative z-10 w-full max-w-7xl mx-auto px-4 pb-8 sm:pb-12 lg:pb-16"
          style={{
            opacity: heroAnim.isVisible ? 1 : 0,
            transform: heroAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
          }}
        >
          <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-1.5 sm:py-2 border border-[#D4AF37]/60 rounded-full text-[#D4AF37] text-xs sm:text-sm font-semibold tracking-widest uppercase mb-3 sm:mb-5">
            <i className="ri-seedling-line"></i>
            Grow It Right
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-3 sm:mb-4 leading-tight max-w-3xl">
            Everything You Need to<br />
            <span className="text-[#D4AF37]">Grow Like a Pro</span>
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 max-w-2xl mb-6 sm:mb-8 leading-relaxed">
            Premium grow lights, organic soils, natural fertilizers, and professional equipment — curated by our master growers for every skill level.
          </p>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <a
              href="#products"
              className="group bg-[#2D5F3F] text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-[#D4AF37] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg text-sm sm:text-base"
            >
              Shop All Equipment
              <i className="ri-arrow-down-line text-xl group-hover:translate-y-1 transition-transform duration-300"></i>
            </a>
            <Link
              to="/guides"
              className="group border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-semibold hover:bg-white hover:text-[#2D5F3F] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer text-sm sm:text-base"
            >
              <i className="ri-book-open-line text-xl"></i>
              Grow Guides
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Tips Bar */}
      <section
        ref={tipsAnim.ref as React.RefObject<HTMLElement>}
        className="bg-[#1A3A2A] py-12 sm:py-16 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-center text-white text-xl sm:text-2xl font-bold mb-8 sm:mb-10 tracking-wide uppercase">
            <i className="ri-lightbulb-line text-[#D4AF37] mr-2"></i>
            Master Grower Tips
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {tips.map((tip, i) => (
              <div
                key={i}
                className="bg-white/10 rounded-xl p-5 sm:p-6 hover:bg-white/20 transition-all duration-300 hover:-translate-y-1 group"
                style={{
                  opacity: tipsAnim.visibleItems[i] ? 1 : 0,
                  transform: tipsAnim.visibleItems[i] ? 'translateY(0)' : 'translateY(30px)',
                  transition: `opacity 0.6s ease-out ${i * 120}ms, transform 0.6s ease-out ${i * 120}ms, background 0.3s, translate 0.3s`,
                }}
              >
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i
                    className={`${tip.icon} text-3xl sm:text-4xl group-hover:scale-110 transition-transform duration-300`}
                    style={{ color: tip.color }}
                  ></i>
                </div>
                <h3 className="text-white font-bold text-base sm:text-lg mb-2">{tip.title}</h3>
                <p className="text-white/75 text-sm leading-relaxed">{tip.tip}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section
        id="products"
        ref={catsAnim.ref as React.RefObject<HTMLElement>}
        className="py-8 sm:py-12 px-4 bg-[#F8FAF9] border-b border-gray-100"
        style={{
          opacity: catsAnim.isVisible ? 1 : 0,
          transform: catsAnim.isVisible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'opacity 0.7s ease-out, transform 0.7s ease-out',
        }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap sm:justify-center scrollbar-hide">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 rounded-full font-semibold text-xs sm:text-sm transition-all duration-300 cursor-pointer whitespace-nowrap hover:scale-105 active:scale-95 flex-shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-[#2D5F3F] text-white shadow-lg scale-105'
                    : 'bg-white text-[#2C2C2C] hover:bg-[#2D5F3F] hover:text-white shadow-sm'
                }`}
              >
                <i className={`${cat.icon} text-sm sm:text-base`}></i>
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-12 sm:py-16 lg:py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 sm:mb-10 gap-3">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#2C2C2C]">
              {categories.find((c) => c.id === activeCategory)?.label ?? 'All Equipment'}
              <span className="ml-2 sm:ml-3 text-base sm:text-xl font-normal text-gray-400">({filtered.length} items)</span>
            </h2>
            <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-500">
              <i className="ri-shield-check-line text-[#2D5F3F] text-base sm:text-lg"></i>
              <span className="hidden sm:inline">Free worldwide shipping on orders over $150</span>
              <span className="sm:hidden">Free shipping over $150</span>
            </div>
          </div>

          <div
            ref={productsAnim.ref as React.RefObject<HTMLDivElement>}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8"
            data-product-shop
          >
            {filtered.map((product, index) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-400 overflow-hidden group hover:-translate-y-3 cursor-pointer"
                style={{
                  opacity: productsAnim.visibleItems[index] ? 1 : 0,
                  transform: productsAnim.visibleItems[index]
                    ? 'translateY(0) scale(1)'
                    : 'translateY(40px) scale(0.97)',
                  transition: `opacity 0.55s ease-out ${(index % 3) * 100}ms, transform 0.55s ease-out ${(index % 3) * 100}ms, box-shadow 0.3s`,
                }}
              >
                {/* Image */}
                <div className="relative w-full h-56 sm:h-64 overflow-hidden bg-[#F8FAF9]">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-600"
                  />
                  {/* Badge */}
                  <span className={`absolute top-3 sm:top-4 left-3 sm:left-4 px-2.5 sm:px-3 py-1 ${product.badgeColor} text-white text-xs font-bold rounded-full shadow`}>
                    {product.badge}
                  </span>
                  {/* Wishlist */}
                  <button
                    onClick={() => toggleWishlist(product.id)}
                    className="absolute top-3 sm:top-4 right-3 sm:right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-md hover:scale-110 transition-all duration-300 cursor-pointer opacity-0 group-hover:opacity-100"
                  >
                    <i
                      className={`${wishlist.includes(product.id) ? 'ri-heart-fill text-red-500' : 'ri-heart-line text-gray-400'} text-lg`}
                    ></i>
                  </button>
                  {/* Quick view overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <button className="bg-white text-[#2D5F3F] px-4 sm:px-5 py-2 rounded-full font-semibold text-xs sm:text-sm hover:bg-[#D4AF37] hover:text-white transition-all duration-300 translate-y-4 group-hover:translate-y-0 transition-transform whitespace-nowrap cursor-pointer">
                      Quick View
                    </button>
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 sm:p-6">
                  <h3 className="text-base sm:text-lg font-bold text-[#2C2C2C] mb-2 line-clamp-2 group-hover:text-[#2D5F3F] transition-colors duration-300 leading-snug">
                    {product.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 line-clamp-2 leading-relaxed">
                    {product.description}
                  </p>

                  {/* Features */}
                  <div className="flex flex-wrap gap-1 mb-3 sm:mb-4">
                    {product.features.slice(0, 2).map((f) => (
                      <span key={f} className="px-2 py-1 bg-[#2D5F3F]/8 text-[#2D5F3F] text-xs rounded-md font-medium">
                        {f}
                      </span>
                    ))}
                  </div>

                  {/* Rating */}
                  <div className="flex items-center gap-2 mb-3 sm:mb-4">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <i
                          key={star}
                          className={`ri-star-fill text-xs sm:text-sm ${star <= Math.round(product.rating) ? 'text-[#D4AF37]' : 'text-gray-200'}`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs sm:text-sm font-semibold text-[#2C2C2C]">{product.rating}</span>
                    <span className="text-xs text-gray-400">({product.reviews})</span>
                  </div>

                  {/* Price + Cart */}
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-xl sm:text-2xl font-bold text-[#2D5F3F]">${product.price}</span>
                      <span className="ml-2 text-xs sm:text-sm text-gray-400 line-through">${product.originalPrice}</span>
                    </div>
                    <button className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-[#2D5F3F] text-white flex items-center justify-center hover:bg-[#D4AF37] hover:scale-110 hover:rotate-12 transition-all duration-300 cursor-pointer">
                      <i className="ri-shopping-cart-line text-base sm:text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Seeds Affiliate Banners */}
      <section className="py-14 sm:py-16 px-4 bg-[#F8FAF9]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#2D5F3F]/30 rounded-full text-[#2D5F3F] text-xs font-semibold tracking-widest uppercase mb-3">
              <i className="ri-seedling-line"></i>
              Trusted Seed Partners
            </span>
            <h2 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C]">Premium Cannabis Seeds</h2>
            <p className="text-gray-500 mt-2 text-sm sm:text-base">Start your grow with quality genetics from our trusted seed bank partners</p>
          </div>

          {/* Crop King & Rocket Seeds — side by side */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto mb-8">
            {/* Crop King Seeds */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <p className="text-[#2D5F3F] text-xs font-bold uppercase tracking-widest mb-4">Crop King Seeds</p>
              <a
                href="https://www.cropkingseeds.com/mix-and-match/feminized-mix-marijuana-seeds/#a_aid=ElixirSue&a_bid=25b3944c"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 hover:scale-105 transition-all duration-300"
              >
                <img
                  src="//www.greenaffiliates.com/accounts/default1/nxkkkeyb/25b3944c.png"
                  alt="Crop King Seeds - Feminized Marijuana Seed Mix"
                  title="Crop King Seeds - Feminized Marijuana Seed Mix"
                  width={250}
                  height={250}
                  className="mx-auto rounded-xl shadow-lg"
                />
              </a>
              <img alt="" src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=25b3944c" width={1} height={1} />
              <p className="text-gray-400 text-xs mt-4">Mix &amp; Match Feminized Seeds</p>
            </div>

            {/* Rocket Seeds */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-md text-center hover:shadow-lg transition-shadow duration-300">
              <p className="text-[#2D5F3F] text-xs font-bold uppercase tracking-widest mb-4">Rocket Seeds</p>
              <a
                href="https://rocketseeds.com/#a_aid=ElixirSue&a_bid=13d1fb1f"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block hover:opacity-80 hover:scale-105 transition-all duration-300"
              >
                <img
                  src="//www.greenaffiliates.com/accounts/default1/nxkkkeyb/13d1fb1f.jpg"
                  alt="Rocket Seeds - MJ for Sale 250x250"
                  title="Rocket Seeds - MJ for Sale 250x250"
                  width={250}
                  height={250}
                  className="mx-auto rounded-xl shadow-lg"
                />
              </a>
              <img alt="" src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=13d1fb1f" width={1} height={1} />
              <p className="text-gray-400 text-xs mt-4">Premium Cannabis Seeds</p>
            </div>
          </div>

          {/* BC Bud Depot — full-width leaderboard */}
          <div className="text-center">
            <p className="text-[#2D5F3F] text-xs font-bold uppercase tracking-widest mb-4">BC Bud Depot</p>
            <a
              href="https://bcbuddepot.com/#a_aid=ElixirSue&a_bid=4edd814d"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block hover:opacity-80 hover:scale-[1.01] transition-all duration-300"
            >
              <img
                src="//www.greenaffiliates.com/accounts/default1/nxkkkeyb/4edd814d.jpg"
                alt="BC Bud Depot Best Seller Marijuana Seeds (728x90)"
                title="BC Bud Depot Best Seller Marijuana Seeds (728x90)"
                width={728}
                height={90}
                className="mx-auto rounded-lg shadow-md max-w-full"
              />
            </a>
            <img alt="" src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=4edd814d" width={1} height={1} />
          </div>
        </div>
      </section>

      {/* A Pot for Pot — Equipment Banner */}
      <section className="py-12 sm:py-14 px-4 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#2D5F3F]/30 rounded-full text-[#2D5F3F] text-xs font-semibold tracking-widest uppercase mb-4">
            <i className="ri-tools-line"></i>
            Growing Equipment Partner
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-[#2C2C2C] mb-2">Complete Grow Kits</h2>
          <p className="text-gray-500 text-sm sm:text-base mb-8">Everything you need in one kit — from seedling to harvest</p>
          <a
            href="https://apotforpot.com/#a_aid=ElixirSue&a_bid=d099e087"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block hover:opacity-80 hover:scale-[1.01] transition-all duration-300"
          >
            <img
              src="//www.greenaffiliates.com/accounts/default1/nxkkkeyb/d099e087.jpg"
              alt="A Pot for Pot - Complete Cannabis Grow Kits"
              title="A Pot for Pot"
              width={728}
              height={90}
              className="mx-auto rounded-lg shadow-md max-w-full"
            />
          </a>
          <img alt="" src="https://www.greenaffiliates.com/scripts/nxkkkeyi?a_aid=ElixirSue&a_bid=d099e087" width={1} height={1} />
        </div>
      </section>

      {/* Why Grow Organic CTA */}
      <section
        ref={ctaAnim.ref as React.RefObject<HTMLElement>}
        className="py-16 sm:py-20 lg:py-24 px-4 relative overflow-hidden"
        style={{
          opacity: ctaAnim.isVisible ? 1 : 0,
          transform: ctaAnim.isVisible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'opacity 0.8s ease-out, transform 0.8s ease-out',
        }}
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=close%20up%20of%20healthy%20vibrant%20green%20cannabis%20plant%20leaves%20with%20morning%20dew%20drops%20in%20soft%20natural%20sunlight%20bokeh%20background%20organic%20cultivation%20beautiful%20nature%20macro%20photography%20lush%20green%20tones%20peaceful%20serene&width=1920&height=600&seq=grow_cta_001&orientation=landscape"
            alt="Organic Growing"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A3A2A]/95 via-[#1A3A2A]/80 sm:via-[#1A3A2A]/70 to-[#1A3A2A]/60 sm:to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="max-w-xl">
            <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 border border-[#D4AF37]/60 rounded-full text-[#D4AF37] text-xs sm:text-sm font-semibold mb-4 sm:mb-6">
              <i className="ri-leaf-line"></i>
              Why Go Organic?
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6 leading-tight">
              Grow Cleaner,<br />Harvest Better
            </h2>
            <p className="text-base sm:text-lg text-white/85 mb-6 sm:mb-8 leading-relaxed">
              Organic growing methods produce richer terpene profiles, cleaner end products, and healthier soil ecosystems. Our curated equipment and nutrients are chosen by master growers who live and breathe organic cultivation.
            </p>
            <div className="flex flex-col gap-2.5 sm:gap-3 mb-8 sm:mb-10">
              {[
                'No synthetic pesticides or harsh chemicals',
                'Richer flavors and terpene expression',
                'Sustainable for the environment',
                'Safer for you and your community',
              ].map((point) => (
                <div key={point} className="flex items-center gap-3 text-white/90">
                  <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center flex-shrink-0">
                    <i className="ri-checkbox-circle-fill text-[#D4AF37] text-lg sm:text-xl"></i>
                  </div>
                  <span className="text-sm sm:text-base">{point}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Link
                to="/guides"
                className="group bg-[#D4AF37] text-[#1A3A2A] px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold hover:bg-white hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer shadow-lg text-sm sm:text-base"
              >
                Read Grow Guides
                <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform duration-300"></i>
              </Link>
              <Link
                to="/about"
                className="group border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold hover:bg-white hover:text-[#2D5F3F] hover:scale-105 transition-all duration-300 flex items-center justify-center gap-3 whitespace-nowrap cursor-pointer text-sm sm:text-base"
              >
                Meet Our Growers
              </Link>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes kenburns {
          from { transform: scale(1.05) translateX(0); }
          to { transform: scale(1.12) translateX(-1%); }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}